import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders } from './api';
import { printDailySummary, printOrderTickets } from './thermalPrinter';
import NumericKeypad from './NumericKeypad';
import TerminalPaymentDialog from './TerminalPaymentDialog';
import { getTerminalConfig, getTerminalReceipt, getTerminalScope, persistPendingTerminalPayment, readPendingTerminalPayment } from './terminalPayments';
import { paymentDisplayMessage } from './paymentDisplay';
import AdminDashboard from './AdminDashboard';
import { downloadAuthenticatedFile } from './downloadAuthenticatedFile';
import bebornePanelLogo from './assets/branding-v5/bekiosk.svg';
const PANEL_TABS = [
  { id: 'en_cours', label: 'En cours' },
  { id: 'terminee', label: 'Terminées' },
  { id: 'annulee', label: 'Tickets annulés' },
  { id: 'admin', label: 'Admin' },
  { id: 'historique', label: 'Historique' },
  { id: 'produits', label: 'Produits' },
];

function formatDateTime(value) {
  return new Date(value).toLocaleString('fr-FR');
}

function formatEuro(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

const EMPTY_PANEL_STATS = Object.freeze({
  inProgress: 0,
  paid: 0,
  completed: 0,
  cancelled: 0,
  cardTotal: 0,
  cashTotal: 0,
  ordersCount: 0,
});

function toLocalIsoDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizePanelStats(payload) {
  return {
    inProgress: Number(payload?.inProgress || 0),
    paid: Number(payload?.paid || 0),
    completed: Number(payload?.completed || 0),
    cancelled: Number(payload?.cancelled || 0),
    cardTotal: Number(payload?.cardTotal || 0),
    cashTotal: Number(payload?.cashTotal || 0),
    ordersCount: Number(payload?.ordersCount || 0),
  };
}

function buildStatsQuery(filters = {}) {
  const query = {};
  if (filters.dateFrom) query.dateFrom = String(filters.dateFrom).trim();
  if (filters.dateTo) query.dateTo = String(filters.dateTo).trim();
  if (filters.productId) query.productId = String(filters.productId).trim();
  const normalizedSearch = String(filters.search || '').trim();
  if (normalizedSearch) query.search = normalizedSearch;
  return query;
}

function getOrderStatusLabel(status) {
  if (status === 'terminee') {
    return 'Terminée';
  }
  if (status === 'prete') {
    return 'Prête';
  }
  if (status === 'annulee') {
    return 'Annulée';
  }
  return 'En cours';
}

function getUberDirectStatusLabel(status) {
  const labels = {
    awaiting_payment: 'Uber : paiement attendu', dispatching: 'Uber : recherche coursier', pending: 'Uber : en attente',
    pickup: 'Uber : vers le restaurant', pickup_complete: 'Uber : commande récupérée', dropoff: 'Uber : en livraison',
    delivered: 'Uber : livrée', canceled: 'Uber : annulée', cancelled: 'Uber : annulée', dispatch_failed: 'Uber : envoi à relancer',
  };
  return labels[String(status || '').toLowerCase()] || `Uber : ${String(status || 'à déclencher').replaceAll('_', ' ')}`;
}

function getOrderPaymentBadge(order) {
  if (!order) {
    return { label: 'À encaisser', className: 'is-unpaid' };
  }

  if (order.status === 'annulee') {
    return { label: 'Annulée', className: 'is-neutral' };
  }

  if (order.paymentStatus === 'paid') {
    return {
      label: `Payée ${order.paymentMethod?.toUpperCase() || ''}`,
      className: 'is-paid',
    };
  }

  return { label: 'À encaisser', className: 'is-unpaid' };
}

function buildMyposReceiptFromOrder(order = {}) {
  const state = String(order.myposStatus || '').trim().toLowerCase();
  if (String(order.paymentMethod || '').trim().toLowerCase() !== 'cb' || state !== 'approved') {
    return null;
  }
  return {
    state: 'approved',
    cardBrand: order.myposCardScheme || '',
    maskedPan: order.myposMaskedPan || '',
    amount: Number(order.totalTtc || 0),
    currency: 'EUR',
    authCode: order.myposAuthCode || '',
    rrn: order.myposRrn || '',
    paymentReference: order.myposPaymentReference || '',
  };
}

function fetchWithAuth(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: buildKioskAuthHeaders(options.headers),
  });
}

function getPreferredClientPrinter(printers) {
  if (!Array.isArray(printers) || printers.length === 0) {
    return null;
  }

  return (
    printers.find((printer) => /principale|caisse|client/i.test(printer.name || '')) ||
    printers[0]
  );
}

function mapOrderToPrintableItems(order) {
  return (order?.OrderItems || []).map((item) => ({
    id: item.productId || item.id,
    name: item.name,
    kitchenName: item.kitchenName || item.Product?.kitchenName || '',
    image: item.Product?.image || '',
    totalPrice: Number(item.unitPrice || item.totalPrice || 0),
    quantity: Number(item.quantity || 1),
    isMenu: !!item.isMenu,
    printerId: item.printerId || item.Printer?.id || item.Product?.printerId || null,
    productionZoneId: item.productionZoneId || item.ProductionZone?.id || item.Product?.productionZoneId || null,
    customSelections: Array.isArray(item.selections)
      ? item.selections.filter((selection) => selection?.sectionName)
      : [],
    selectedSupplements: Array.isArray(item.selections)
      ? item.selections.filter((selection) => selection?.kind === 'supplement')
      : [],
  }));
}

function PanelView() {
  const navigate = useNavigate();
  const [terminalScope] = useState(getTerminalScope);
  const [terminalOrderId, setTerminalOrderId] = useState(() => readPendingTerminalPayment()?.orderId || null);
  const [activeTab, setActiveTab] = useState('en_cours');
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [showCashPaymentModal, setShowCashPaymentModal] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [clientPrinters, setClientPrinters] = useState([]);
  const [myposFeedback, setMyposFeedback] = useState('');
  const [ticketHeaderCashText, setTicketHeaderCashText] = useState('');
  const [ticketHeaderCardPaidText, setTicketHeaderCardPaidText] = useState('');
  const [ticketHeaderDuplicateText, setTicketHeaderDuplicateText] = useState('');
  const [ticketLogoMedia, setTicketLogoMedia] = useState('');
  const [ticketStoreLine1, setTicketStoreLine1] = useState('MAYTHAI');
  const [ticketStoreLine2, setTicketStoreLine2] = useState('32 avenue de la Republique');
  const [ticketStoreLine3, setTicketStoreLine3] = useState('75011 Paris');
  const [ticketStoreLine4, setTicketStoreLine4] = useState('TVA : - NAF : 5610A');
  const [ticketStoreLine5, setTicketStoreLine5] = useState('Tel: 01 47 00 19 65');
  const [ticketStoreLine6, setTicketStoreLine6] = useState('');
  const [ticketQrText, setTicketQrText] = useState('');
  const [ticketQrUrl, setTicketQrUrl] = useState('');
  const [ticketFooterLine1, setTicketFooterLine1] = useState('Merci de votre visite');
  const [ticketFooterLine2, setTicketFooterLine2] = useState('A bientot !');
  const [ticketFooterLine3, setTicketFooterLine3] = useState('');
  const [ticketHeaderTextSize, setTicketHeaderTextSize] = useState(3);
  const [productionZones, setProductionZones] = useState([]);
  const [clientTicketZoneId, setClientTicketZoneId] = useState('');
  const [dailySummaryPrinterId, setDailySummaryPrinterId] = useState('');
  const [dailySummaryPrinting, setDailySummaryPrinting] = useState(false);
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [productFilters, setProductFilters] = useState({
    sectionId: '',
    subcategoryId: '',
    availability: 'all',
  });
  const [historyFilters, setHistoryFilters] = useState({
    dateFrom: '',
    dateTo: '',
    productId: '',
    search: '',
  });
  const [todayStats, setTodayStats] = useState({ ...EMPTY_PANEL_STATS });
  const [historyStats, setHistoryStats] = useState({ ...EMPTY_PANEL_STATS });
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productAvailabilityUpdating, setProductAvailabilityUpdating] = useState(false);
  const [panelDialog, setPanelDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmer',
    onConfirm: null,
  });
  const [dialogReason, setDialogReason] = useState('');
  const [espBridgeStatus, setEspBridgeStatus] = useState({
    managerReachable: false,
    statuses: [],
    hasOffline: false,
  });
  const [espWarningDismissedFor, setEspWarningDismissedFor] = useState('');

  const closePanelDialog = useCallback(() => {
    setPanelDialog({
      isOpen: false,
      title: '',
      message: '',
      confirmLabel: 'Confirmer',
      onConfirm: null,
    });
  }, []);

  const showPanelNotice = useCallback((message, title = 'Information') => {
    setPanelDialog({
      isOpen: true,
      title,
      message: paymentDisplayMessage(message).trim() || 'Opération impossible.',
      confirmLabel: 'Fermer',
      onConfirm: null,
    });
  }, []);

  const showPanelConfirm = useCallback(({ title, message, confirmLabel = 'Confirmer', onConfirm, requiresReason = false }) => {
    setDialogReason('');
    setPanelDialog({
      isOpen: true,
      title: String(title || '').trim() || 'Confirmation',
      message: String(message || '').trim(),
      confirmLabel: String(confirmLabel || '').trim() || 'Confirmer',
      onConfirm: typeof onConfirm === 'function' ? onConfirm : null,
      requiresReason,
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/admin');
    }
  }, [navigate]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/orders`);
      if (!response.ok) {
        throw new Error('Impossible de charger les commandes.');
      }

      const payload = await response.json();
      setOrders(payload);
      setSelectedOrderId((current) => current ?? payload[0]?.id ?? null);
      setError('');
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrderStats = useCallback(async (query = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      const normalizedValue = String(value || '').trim();
      if (normalizedValue) {
        searchParams.set(key, normalizedValue);
      }
    });
    const response = await fetchWithAuth(`${API_URL}/api/orders/stats?${searchParams.toString()}`);
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || 'Impossible de charger les statistiques commandes.');
    }
    return normalizePanelStats(payload);
  }, []);

  const refreshTodayStats = useCallback(async () => {
    try {
      const today = toLocalIsoDate(new Date());
      const stats = await fetchOrderStats({ dateFrom: today, dateTo: today });
      setTodayStats(stats);
    } catch (statsError) {
      setTodayStats({ ...EMPTY_PANEL_STATS });
    }
  }, [fetchOrderStats]);

  const fetchPrinters = useCallback(async () => {
    const response = await fetchWithAuth(`${API_URL}/api/printers`);
    if (!response.ok) {
      throw new Error('Impossible de charger les imprimantes.');
    }
    setClientPrinters(await response.json());
  }, []);

  const fetchEspBridgeStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/esp-bridges/status`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Etat modules indisponible.');
      }
      setEspBridgeStatus({
        managerReachable: !!payload.managerReachable,
        statuses: Array.isArray(payload.statuses) ? payload.statuses : [],
        hasOffline: !!payload.hasOffline,
      });
    } catch (error) {
      setEspBridgeStatus({
        managerReachable: false,
        statuses: [],
        hasOffline: false,
      });
    }
  }, []);

  const fetchPrinterSettings = useCallback(async () => {
    const response = await fetchWithAuth(`${API_URL}/api/printer-settings`);
    if (!response.ok) {
      throw new Error('Impossible de charger les options ticket.');
    }
    const payload = await response.json();
    setTicketHeaderCashText(payload.ticketHeaderCashText || payload.ticketHeaderText || '');
    setTicketHeaderCardPaidText(payload.ticketHeaderCardPaidText || payload.ticketHeaderText || '');
    setTicketHeaderDuplicateText(payload.ticketHeaderDuplicateText || payload.ticketHeaderText || '');
    setTicketLogoMedia(payload.ticketLogoMedia || '');
    setTicketStoreLine1(payload.ticketStoreLine1 || 'MAYTHAI');
    setTicketStoreLine2(payload.ticketStoreLine2 || '32 avenue de la Republique');
    setTicketStoreLine3(payload.ticketStoreLine3 || '75011 Paris');
    setTicketStoreLine4(payload.ticketStoreLine4 || 'TVA : - NAF : 5610A');
    setTicketStoreLine5(payload.ticketStoreLine5 || 'Tel: 01 47 00 19 65');
    setTicketStoreLine6(payload.ticketStoreLine6 || '');
    setTicketQrText(payload.ticketQrText || '');
    setTicketQrUrl(payload.ticketQrUrl || '');
    setTicketFooterLine1(payload.ticketFooterLine1 || 'Merci de votre visite');
    setTicketFooterLine2(payload.ticketFooterLine2 || 'A bientot !');
    setTicketFooterLine3(payload.ticketFooterLine3 || '');
    setTicketHeaderTextSize(payload.ticketHeaderTextSize || 3);
    setClientTicketZoneId(payload.clientTicketZoneId || '');
    setDailySummaryPrinterId(payload.dailySummaryPrinterId || '');
  }, []);

  const fetchDailySummary = useCallback(async (date) => {
    const query = new URLSearchParams();
    if (date) {
      query.set('date', date);
    }

    const response = await fetchWithAuth(`${API_URL}/api/reports/daily-summary?${query.toString()}`);
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || 'Impossible de charger le récap journalier.');
    }
    return payload;
  }, []);

  const fetchProductsData = useCallback(async () => {
    const [productsRes, sectionsRes, subcategoriesRes] = await Promise.all([
      fetchWithAuth(`${API_URL}/api/products`),
      fetchWithAuth(`${API_URL}/api/sections`),
      fetchWithAuth(`${API_URL}/api/subcategories`),
    ]);

    if (!productsRes.ok || !sectionsRes.ok || !subcategoriesRes.ok) {
      throw new Error('Impossible de charger le catalogue produits.');
    }

    setProducts(await productsRes.json());
    setSections(await sectionsRes.json());
    setSubcategories(await subcategoriesRes.json());
  }, []);

  useEffect(() => {
    fetchOrders();
    refreshTodayStats();
    fetchPrinters().catch((loadError) => setError(loadError.message));
    fetchEspBridgeStatus();
    fetchPrinterSettings().catch((loadError) => setError(loadError.message));
    fetchProductsData().catch(() => {
      setError('Impossible de charger le catalogue produits.');
    });
    fetchWithAuth(`${API_URL}/api/production-zones`)
      .then((response) => response.json())
      .then((payload) => setProductionZones(Array.isArray(payload) ? payload : []))
      .catch(() => setProductionZones([]));
    const intervalId = window.setInterval(() => {
      fetchOrders();
      refreshTodayStats();
      fetchEspBridgeStatus();
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [fetchOrders, refreshTodayStats, fetchPrinters, fetchEspBridgeStatus, fetchPrinterSettings, fetchProductsData]);

  const visibleOrders = useMemo(() => {
    const filtered = activeTab === 'en_cours'
      ? orders.filter((order) => ['en_cours', 'prete'].includes(order.status))
      : activeTab === 'terminee'
        ? orders.filter((order) => order.status === 'terminee')
        : activeTab === 'annulee'
          ? orders.filter((order) => order.status === 'annulee')
          : orders;
    return filtered.sort((a, b) => {
      if (activeTab === 'en_cours') {
        return new Date(a.orderedAt) - new Date(b.orderedAt);
      }
      return new Date(b.completedAt || b.orderedAt) - new Date(a.completedAt || a.orderedAt);
    });
  }, [orders, activeTab]);

  const filteredSubcategories = useMemo(
    () =>
      productFilters.sectionId
        ? subcategories.filter((subcategory) => String(subcategory.sectionId) === String(productFilters.sectionId))
        : subcategories,
    [subcategories, productFilters.sectionId]
  );

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        if (productFilters.sectionId && String(product.sectionId) !== String(productFilters.sectionId)) {
          return false;
        }

        if (productFilters.subcategoryId && String(product.subcategoryId || '') !== String(productFilters.subcategoryId)) {
          return false;
        }

        if (productFilters.availability === 'available' && product.isCurrentlyAvailable === false) {
          return false;
        }

        if (productFilters.availability === 'unavailable' && product.isCurrentlyAvailable !== false) {
          return false;
        }

        return true;
      }),
    [products, productFilters.sectionId, productFilters.subcategoryId, productFilters.availability]
  );

  const offlineEspStatuses = useMemo(
    () => (Array.isArray(espBridgeStatus.statuses) ? espBridgeStatus.statuses.filter((status) => !status.online) : []),
    [espBridgeStatus.statuses]
  );
  const offlineEspKey = useMemo(
    () => offlineEspStatuses.map((status) => String(status.bridgeId || status.printerId || '')).join('|'),
    [offlineEspStatuses]
  );
  const showEspWarningModal = Boolean(offlineEspKey) && espWarningDismissedFor !== offlineEspKey;
  const getEspLedClassName = (code = '') => {
    const normalized = String(code || '').trim().toLowerCase();
    if (normalized === 'green_fixed') return 'is-green';
    if (normalized === 'orange_blink') return 'is-orange';
    if (normalized === 'blue_blink') return 'is-blue';
    if (normalized === 'magenta_fixed') return 'is-magenta';
    if (normalized === 'red_blink') return 'is-red';
    return 'is-neutral';
  };

  const historyOrders = useMemo(() => {
    const normalizedSearch = String(historyFilters.search || '').trim().toLowerCase();
    const dateFrom = historyFilters.dateFrom ? new Date(`${historyFilters.dateFrom}T00:00:00`) : null;
    const dateTo = historyFilters.dateTo ? new Date(`${historyFilters.dateTo}T23:59:59.999`) : null;
    const productIdFilter = String(historyFilters.productId || '').trim();

    return orders
      .filter((order) => {
        const orderedAt = new Date(order.orderedAt);
        if (dateFrom && orderedAt < dateFrom) {
          return false;
        }
        if (dateTo && orderedAt > dateTo) {
          return false;
        }
        if (productIdFilter && !order.OrderItems.some((item) => String(item.productId) === productIdFilter)) {
          return false;
        }
        if (normalizedSearch && !order.OrderItems.some((item) => {
          const name = String(item.name || '').toLowerCase();
          const kitchenName = String(item.kitchenName || '').toLowerCase();
          return name.includes(normalizedSearch) || kitchenName.includes(normalizedSearch);
        })) {
          return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
  }, [orders, historyFilters]);

  const displayedStats = activeTab === 'historique' ? historyStats : todayStats;

  const selectedFilteredProductCount = filteredProducts.filter((product) =>
    selectedProductIds.some((id) => String(id) === String(product.id))
  ).length;

  const allFilteredProductsSelected =
    filteredProducts.length > 0 && selectedFilteredProductCount === filteredProducts.length;

  useEffect(() => {
    if (activeTab === 'produits' || activeTab === 'historique') {
      return;
    }
    if (!visibleOrders.some((order) => order.id === selectedOrderId)) {
      setSelectedOrderId(visibleOrders[0]?.id ?? null);
      setCashReceived('');
      setShowCashPaymentModal(false);
      setMyposFeedback('');
    }
  }, [visibleOrders, selectedOrderId, activeTab]);

  useEffect(() => {
    setSelectedProductIds((current) =>
      current.filter((id) => products.some((product) => String(product.id) === String(id)))
    );
  }, [products]);

  useEffect(() => {
    if (activeTab !== 'historique') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      fetchOrderStats(buildStatsQuery(historyFilters))
        .then((stats) => setHistoryStats(stats))
        .catch(() => setHistoryStats({ ...EMPTY_PANEL_STATS }));
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, historyFilters, fetchOrderStats]);

  const selectedOrder = visibleOrders.find((order) => order.id === selectedOrderId) || null;
  const cashValue = Number(cashReceived || 0);
  const cashChange =
    selectedOrder && Number.isFinite(cashValue)
      ? Number((cashValue - Number(selectedOrder.totalTtc)).toFixed(2))
      : null;
  const selectedOrderHasPendingMypos = selectedOrder?.myposStatus === 'pending';

  const closeCashPaymentModal = useCallback(() => {
    setShowCashPaymentModal(false);
    setCashReceived('');
  }, []);

  const updateLocalOrder = useCallback((updatedOrder) => {
    setOrders((current) => current.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)));
  }, []);

  const handleClientTicketPrint = useCallback(async (order, mode = 'paid', terminalReceipt = null) => {
    const stripeReceipt = terminalReceipt || (order?.physicalPaymentProvider === 'stripe_terminal' ? await getTerminalReceipt(fetchWithAuth, order.id, terminalScope) : null);
    const targetZone = productionZones.find((zone) => String(zone.id) === String(clientTicketZoneId));
    const targetPrinter =
      targetZone?.Printer ||
      clientPrinters.find((printer) => String(printer.id) === String(targetZone?.printerId)) ||
      getPreferredClientPrinter(clientPrinters);

    if (!targetPrinter) {
      throw new Error('Aucune imprimante disponible pour le ticket client.');
    }

    const normalizedPaymentMethod = String(order?.paymentMethod || '').trim().toLowerCase();
    const isCashPayment = normalizedPaymentMethod === 'espece' || normalizedPaymentMethod === 'cash';
    const isAlreadyPaid = String(order?.paymentStatus || '').trim().toLowerCase() === 'paid';
    const defaultCashPendingHeader = ticketHeaderCashText || 'A PAYER ESPECE';
    const defaultCashPaidHeader = 'DEJA PAYE ESPECE';
    const defaultCardPaidHeader = ticketHeaderCardPaidText || 'PAYE CB';
    let ticketHeaderText = defaultCardPaidHeader;

    if (isCashPayment && isAlreadyPaid) {
      // Paid cash receipts must always show the paid-cash header, including reprints.
      ticketHeaderText = defaultCashPaidHeader;
    } else if (mode === 'duplicate') {
      ticketHeaderText = ticketHeaderDuplicateText || (isCashPayment ? defaultCashPendingHeader : defaultCardPaidHeader);
    } else {
      ticketHeaderText = isCashPayment ? defaultCashPendingHeader : defaultCardPaidHeader;
    }

    await printOrderTickets(
      [targetPrinter],
      mapOrderToPrintableItems(order),
      Number(order.totalTtc).toFixed(2),
      order.ticketNumber,
      {
        ticketHeaderText,
        ticketHeaderTextSize,
        ticketLogoMedia,
        ticketStoreLine1,
        ticketStoreLine2,
        ticketStoreLine3,
        ticketStoreLine4,
        ticketStoreLine5,
        ticketStoreLine6,
        ticketQrText,
        ticketQrUrl,
        ticketFooterLine1,
        ticketFooterLine2,
        ticketFooterLine3,
        customerFirstName: order?.Customer?.firstName || '',
        paymentMethod: order?.paymentMethod || '',
        isAlreadyPaid,
        myposReceipt: stripeReceipt ? null : buildMyposReceiptFromOrder(order),
        stripeReceipt,
        customerComment: order.customerComment || '',
        includeCutlery: !!order.includeCutlery,
        skipProductionTickets: true,
      }
    );
  }, [
    terminalScope,
    clientPrinters,
    productionZones,
    clientTicketZoneId,
    ticketHeaderCashText,
    ticketHeaderCardPaidText,
    ticketHeaderDuplicateText,
    ticketHeaderTextSize,
    ticketLogoMedia,
    ticketStoreLine1,
    ticketStoreLine2,
    ticketStoreLine3,
    ticketStoreLine4,
    ticketStoreLine5,
    ticketStoreLine6,
    ticketQrText,
    ticketQrUrl,
    ticketFooterLine1,
    ticketFooterLine2,
    ticketFooterLine3,
  ]);

  const handlePayment = async (paymentMethod, options = {}) => {
    if (!selectedOrder) {
      return;
    }

    setBusyAction(`payment-${paymentMethod}`);
    setMyposFeedback('');

    try {
      const pendingTerminal = readPendingTerminalPayment(terminalScope);
      if (pendingTerminal) { setTerminalOrderId(pendingTerminal.orderId); return; }
      if (paymentMethod === 'cb') {
        const terminal = await getTerminalConfig(fetchWithAuth, terminalScope);
        if (terminal.enabled) {
          if (!terminal.configured) throw new Error(terminal.message || 'Terminal de paiement à configurer.');
          if (Math.round(Number(selectedOrder.totalTtc) * 100) < 50) throw new Error('Le paiement sur terminal nécessite un ticket d’au moins 0,50 €.');
          persistPendingTerminalPayment({ orderId: Number(selectedOrder.id), state: 'ready', amountCents: Math.round(Number(selectedOrder.totalTtc) * 100), context: { source: 'panel' } }, terminalScope);
          setTerminalOrderId(selectedOrder.id);
          return;
        }
        const purchaseResponse = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/mypos/purchase`, {
          method: 'POST',
        });
        const purchasePayload = await purchaseResponse.json();
        if (!purchaseResponse.ok) {
          throw new Error(purchasePayload.error || 'Lancement du paiement sur le terminal impossible');
        }

        setMyposFeedback('Paiement envoyé au terminal. En attente de validation sur le TPE...');

        let lastOrder = purchasePayload.order || selectedOrder;
        let attempts = 0;

        while (attempts < 45) {
          attempts += 1;
          await new Promise((resolve) => window.setTimeout(resolve, 2000));

          const statusResponse = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/mypos/status`);
          const statusPayload = await statusResponse.json();
          if (!statusResponse.ok) {
            throw new Error(statusPayload.error || 'Suivi du terminal impossible');
          }

          lastOrder = statusPayload.order;
          updateLocalOrder(lastOrder);

          const state = statusPayload.mypos?.state;
          if (state === 'approved') {
            setMyposFeedback('');
            try {
              await handleClientTicketPrint(lastOrder, 'paid');
            } catch (printError) {
              showPanelNotice(`Commande encaissée, mais impression client échouée : ${printError.message}`, 'Impression');
            }
            return;
          }

          if (state === 'failed' || state === 'cancelled' || state === 'error') {
            throw new Error(statusPayload.mypos?.statusMessage || 'Paiement refusé sur le terminal.');
          }

          setMyposFeedback('Paiement en attente sur le terminal...');
        }

        throw new Error('Le terminal n’a pas répondu à temps. Vérifie le TPE puis réessaie.');
      }

      const payload = {
        paymentMethod,
      };

      if (paymentMethod === 'espece') {
        payload.amountPaid = options.exactAmount
          ? Number(selectedOrder.totalTtc || 0)
          : Number(options.amountPaid ?? cashReceived);
      }

      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/payment`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const updatedOrder = await response.json();
      if (!response.ok) {
        throw new Error(updatedOrder.error || 'Encaissement impossible');
      }

      updateLocalOrder(updatedOrder);
      setCashReceived('');
      setShowCashPaymentModal(false);
      try {
        await handleClientTicketPrint(updatedOrder, 'paid');
      } catch (printError) {
        showPanelNotice(`Commande encaissée, mais impression client échouée : ${printError.message}`, 'Impression');
      }
    } catch (paymentError) {
      showPanelNotice(paymentError.message, 'Encaissement');
    } finally {
      setBusyAction('');
    }
  };

  const handlePanelDialogConfirm = async () => {
    if (typeof panelDialog.onConfirm !== 'function') {
      closePanelDialog();
      return;
    }
    const confirmAction = panelDialog.onConfirm;
    if (panelDialog.requiresReason && !dialogReason.trim()) return;
    closePanelDialog();
    await confirmAction(dialogReason.trim());
  };

  const handleCancelMyposPayment = async () => {
    if (!selectedOrder) {
      return;
    }

    setBusyAction('cancel-mypos');

    try {
      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/mypos/cancel`, {
        method: 'POST',
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Annulation du paiement sur le terminal impossible');
      }

      updateLocalOrder(payload.order);
      setMyposFeedback('');
    } catch (cancelError) {
      showPanelNotice(cancelError.message, 'Paiement TPE');
    } finally {
      setBusyAction('');
    }
  };

  const handleReprintClientTicket = async () => {
    if (!selectedOrder) {
      return;
    }

    setBusyAction('reprint-client');

    try {
      await handleClientTicketPrint(selectedOrder, 'duplicate');
    } catch (reprintError) {
      showPanelNotice(reprintError.message, 'Impression');
    } finally {
      setBusyAction('');
    }
  };

  const handleDailySummaryPrint = async () => {
    try {
      setDailySummaryPrinting(true);

      const summary = await fetchDailySummary();
      const targetPrinter = clientPrinters.find(
        (printer) => String(printer.id) === String(dailySummaryPrinterId)
      );

      if (!targetPrinter) {
        throw new Error('Sélectionne une imprimante pour le récap journalier dans Admin > Exploitation > Imprimantes.');
      }

      await printDailySummary(targetPrinter, summary);

      const response = await fetchWithAuth(`${API_URL}/api/printer-settings/mark-daily-summary-printed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: summary.date }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Impossible de marquer le récap comme imprimé.');
      }
      setDailySummaryPrinterId(payload.dailySummaryPrinterId || '');
    } catch (error) {
      showPanelNotice(error.message || 'Erreur lors de l’impression du récap du jour.', 'Récap journalier');
    } finally {
      setDailySummaryPrinting(false);
    }
  };

  const handleStatusChange = async (status) => {
    if (!selectedOrder) {
      return;
    }

    setBusyAction(`status-${status}`);

    try {
      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });

      const updatedOrder = await response.json();
      if (!response.ok) {
        throw new Error(updatedOrder.error || 'Mise à jour impossible');
      }

      updateLocalOrder(updatedOrder);
    } catch (statusError) {
      showPanelNotice(statusError.message, 'Statut commande');
    } finally {
      setBusyAction('');
    }
  };

  const retryUberDirect = async () => {
    if (!selectedOrder) return;
    setBusyAction('uber-direct-retry');
    try {
      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/uber-direct/retry`, { method: 'POST' });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Relance Uber Direct impossible.');
      updateLocalOrder(payload.order);
    } catch (retryError) {
      showPanelNotice(retryError.message, 'Uber Direct');
    } finally {
      setBusyAction('');
    }
  };

  const runCancelCashTicket = useCallback(async (reason) => {
    if (!selectedOrder || selectedOrder.paymentStatus === 'paid') {
      return;
    }

    setBusyAction('status-annulee');

    try {
      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'annulee', reason }),
      });

      const updatedOrder = await response.json();
      if (!response.ok) {
        throw new Error(updatedOrder.error || 'Annulation impossible');
      }

      updateLocalOrder(updatedOrder);
      setCashReceived('');
      setShowCashPaymentModal(false);
      setMyposFeedback('');
    } catch (cancelError) {
      showPanelNotice(cancelError.message, 'Annulation commande');
    } finally {
      setBusyAction('');
    }
  }, [selectedOrder, updateLocalOrder, showPanelNotice]);

  const handleCancelCashTicket = async () => {
    if (!selectedOrder || selectedOrder.paymentStatus === 'paid') {
      return;
    }

    if (selectedOrderHasPendingMypos) {
      showPanelNotice('Annule d’abord le paiement TPE en cours.', 'Annulation commande');
      return;
    }

    showPanelConfirm({
      title: 'Annuler le ticket',
      message: `Annuler la commande #${String(selectedOrder.ticketNumber).padStart(4, '0')} ?`,
      confirmLabel: 'Annuler le ticket',
      onConfirm: runCancelCashTicket,
      requiresReason: true,
    });
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds((current) => {
      const normalizedId = String(productId);
      if (current.some((id) => String(id) === normalizedId)) {
        return current.filter((id) => String(id) !== normalizedId);
      }
      return [...current, productId];
    });
  };

  const toggleAllFilteredProductSelections = () => {
    const filteredIds = filteredProducts.map((product) => String(product.id));
    setSelectedProductIds((current) => {
      const selectedInFilter = current.filter((id) => filteredIds.includes(String(id)));
      if (selectedInFilter.length === filteredIds.length && filteredIds.length > 0) {
        return current.filter((id) => !filteredIds.includes(String(id)));
      }
      const remaining = current.filter((id) => !filteredIds.includes(String(id)));
      return [...remaining, ...filteredProducts.map((product) => product.id)];
    });
  };

  const updateProductsAvailability = async (isAvailable, productIds = []) => {
    if (!productIds.length) {
      showPanelNotice('Sélectionne au moins un produit.', 'Produits');
      return;
    }

    try {
      setProductAvailabilityUpdating(true);
      const response = await fetchWithAuth(`${API_URL}/api/products/availability`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productIds,
          isAvailable,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour de disponibilité impossible.');
      }

      const updatedById = new Map((payload.products || []).map((product) => [String(product.id), product]));
      setProducts((current) =>
        current.map((product) => (updatedById.has(String(product.id)) ? updatedById.get(String(product.id)) : product))
      );
    } catch (error) {
      showPanelNotice(error.message, 'Produits');
    } finally {
      setProductAvailabilityUpdating(false);
    }
  };

  return (
    <div className="panel-page">
      <div className="panel-sticky-head">
        <header className="panel-hero">
          <div className="panel-brand-card" aria-label="BEKIOSK">
            <picture className="suite-brand-v3"><source media="(max-width: 600px)" srcSet={bebornePanelLogo} /><img src="/static/branding-v4/bekiosk/horizontal.svg" alt="BEKIOSK" /></picture>
          </div>
          <div className="panel-hero-stats">
            <article className="panel-stat">
              <strong>{displayedStats.inProgress}</strong>
              <span>En cours</span>
            </article>
            <article className="panel-stat">
              <strong>{displayedStats.paid}</strong>
              <span>Payées</span>
            </article>
            <article className="panel-stat">
              <strong>{displayedStats.completed}</strong>
              <span>Terminées</span>
            </article>
            <article className="panel-stat">
              <strong>{displayedStats.cancelled}</strong>
              <span>Tickets annulés</span>
            </article>
            <article className="panel-stat">
              <strong>{formatEuro(displayedStats.cardTotal)}</strong>
              <span>CB : +</span>
            </article>
            <article className="panel-stat">
              <strong>{formatEuro(displayedStats.cashTotal)}</strong>
              <span>ESPECE : +</span>
            </article>
          </div>
        </header>

        <div className="panel-tabs">
          {PANEL_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`panel-tab ${activeTab === tab.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
          <button
            type="button"
            className="panel-tab panel-tab-summary"
            disabled={dailySummaryPrinting}
            onClick={handleDailySummaryPrint}
          >
            {dailySummaryPrinting ? 'Impression du récap...' : 'Imprimer le récap du jour'}
          </button>
        </div>
      </div>

      {error && <div className="panel-alert">{error}</div>}

      {activeTab === 'admin' ? (
        <section className="panel-admin-tab">
          <AdminDashboard />
        </section>
      ) : activeTab === 'produits' ? (
        <section className="panel-products">
          <div className="panel-products-head">
            <h2>Disponibilité des produits</h2>
            <p>Filtre par catégorie/sous-catégorie et active ou désactive les produits rapidement.</p>
          </div>
          <div className="panel-products-toolbar">
            <label className="admin-select-all">
              <input
                type="checkbox"
                checked={allFilteredProductsSelected}
                onChange={toggleAllFilteredProductSelections}
                aria-label="Sélectionner tous les produits filtrés"
              />
              <span>Tout</span>
            </label>
            <label className="admin-field admin-field-inline-text">
              <span>Catégorie</span>
              <select
                value={productFilters.sectionId}
                onChange={(event) =>
                  setProductFilters((current) => ({
                    ...current,
                    sectionId: event.target.value,
                    subcategoryId: '',
                  }))
                }
              >
                <option value="">Toutes</option>
                {sections.map((section) => (
                  <option key={`panel-section-${section.id}`} value={section.id}>
                    {section.emoji ? `${section.emoji} ` : ''}{section.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field admin-field-inline-text">
              <span>Sous-catégorie</span>
              <select
                value={productFilters.subcategoryId}
                onChange={(event) =>
                  setProductFilters((current) => ({
                    ...current,
                    subcategoryId: event.target.value,
                  }))
                }
              >
                <option value="">Toutes</option>
                {filteredSubcategories.map((subcategory) => (
                  <option key={`panel-subcategory-${subcategory.id}`} value={subcategory.id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field admin-field-inline-text">
              <span>Disponibilité</span>
              <select
                value={productFilters.availability}
                onChange={(event) =>
                  setProductFilters((current) => ({
                    ...current,
                    availability: event.target.value,
                  }))
                }
              >
                <option value="all">Tous</option>
                <option value="available">Disponibles</option>
                <option value="unavailable">Indisponibles</option>
              </select>
            </label>
            <button
              type="button"
              className={`admin-button admin-button-available ${productAvailabilityUpdating ? 'is-loading' : ''}`}
              onClick={() => updateProductsAvailability(true, selectedProductIds)}
              disabled={productAvailabilityUpdating}
            >
              {productAvailabilityUpdating ? 'Mise à jour...' : 'Rendre dispo sélection'}
            </button>
            <button
              type="button"
              className={`admin-button admin-button-unavailable ${productAvailabilityUpdating ? 'is-loading' : ''}`}
              onClick={() => updateProductsAvailability(false, selectedProductIds)}
              disabled={productAvailabilityUpdating}
            >
              {productAvailabilityUpdating ? 'Mise à jour...' : 'Rendre indispo sélection'}
            </button>
            <span className="panel-products-count">
              {filteredProducts.length} produit(s) · {selectedFilteredProductCount} sélectionné(s)
            </span>
          </div>

          <div className="panel-products-grid">
            {filteredProducts.length === 0 ? (
              <div className="panel-empty">Aucun produit pour ces filtres.</div>
            ) : (
              filteredProducts.map((product) => (
                <article className="panel-product-card" key={`panel-product-${product.id}`}>
                  <div className="panel-product-head">
                    <label className="admin-record-select">
                      <input
                        type="checkbox"
                        checked={selectedProductIds.some((id) => String(id) === String(product.id))}
                        onChange={() => toggleProductSelection(product.id)}
                        aria-label={`Sélectionner ${product.name}`}
                      />
                    </label>
                    <div>
                      <h3>{product.name}</h3>
                      <p>
                        {product.Section?.name}
                        {product.Subcategory ? ` / ${product.Subcategory.name}` : ''}
                      </p>
                    </div>
                    <strong>{Number(product.price || 0).toFixed(2)} €</strong>
                  </div>
                  <div className="panel-product-meta">
                    <span className={`panel-status-badge ${product.isCurrentlyAvailable === false ? 'is-unpaid' : 'is-paid'}`}>
                      {product.isCurrentlyAvailable === false ? 'Indisponible' : 'Disponible'}
                    </span>
                    {product.availableFromTime || product.availableToTime ? (
                      <span>
                        Créneau: {product.availableFromTime || '...'} - {product.availableToTime || '...'}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className={`admin-button ${product.isCurrentlyAvailable === false ? 'admin-button-available' : 'admin-button-unavailable'}`}
                      disabled={productAvailabilityUpdating}
                      onClick={() => updateProductsAvailability(product.isCurrentlyAvailable === false, [product.id])}
                    >
                      {product.isCurrentlyAvailable === false ? 'Mettre dispo' : 'Mettre indispo'}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      ) : activeTab === 'historique' ? (
        <section className="panel-history">
          <div className="panel-products-head">
            <h2>Historique des commandes</h2>
            <p>Retrouve les tickets avec filtres date, produit et recherche libre.</p>
          </div>

          <div className="panel-history-toolbar">
            <label className="admin-field admin-field-inline-text">
              <span>Date de début</span>
              <input
                type="date"
                value={historyFilters.dateFrom}
                onChange={(event) =>
                  setHistoryFilters((current) => ({
                    ...current,
                    dateFrom: event.target.value,
                  }))
                }
              />
            </label>
            <label className="admin-field admin-field-inline-text">
              <span>Date de fin</span>
              <input
                type="date"
                value={historyFilters.dateTo}
                onChange={(event) =>
                  setHistoryFilters((current) => ({
                    ...current,
                    dateTo: event.target.value,
                  }))
                }
              />
            </label>
            <label className="admin-field admin-field-inline-text">
              <span>Produit</span>
              <select
                value={historyFilters.productId}
                onChange={(event) =>
                  setHistoryFilters((current) => ({
                    ...current,
                    productId: event.target.value,
                  }))
                }
              >
                <option value="">Tous les produits</option>
                {products.map((product) => (
                  <option key={`panel-history-product-${product.id}`} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-field admin-field-inline-text">
              <span>Recherche</span>
              <input
                type="text"
                placeholder="Nom produit"
                value={historyFilters.search}
                onChange={(event) =>
                  setHistoryFilters((current) => ({
                    ...current,
                    search: event.target.value,
                  }))
                }
              />
            </label>
            <button
              className="admin-button"
              type="button"
              onClick={() => {
                const searchParams = new URLSearchParams();
                Object.entries(historyFilters).forEach(([key, value]) => {
                  if (value) {
                    searchParams.set(key, value);
                  }
                });
                downloadAuthenticatedFile(`${API_URL}/api/orders/export.csv?${searchParams.toString()}`, 'historique-commandes.csv')
                  .catch((exportError) => showPanelNotice(exportError.message, 'Export'));
              }}
            >
              Export CSV
            </button>
          </div>

          <div className="panel-history-list">
            {loading ? (
              <div className="panel-empty">Chargement des commandes...</div>
            ) : historyOrders.length === 0 ? (
              <div className="panel-empty">Aucune commande pour ces filtres.</div>
            ) : (
              historyOrders.map((order) => {
                const paymentBadge = getOrderPaymentBadge(order);
                return (
                <article className="panel-history-card" key={`panel-history-${order.id}`}>
                  <div className="panel-order-card-top">
                    <strong>Commande #{String(order.ticketNumber).padStart(4, '0')}</strong>
                    <span>{formatEuro(order.totalTtc)}</span>
                  </div>
                  <div className="panel-order-card-meta">
                    <span>{formatDateTime(order.orderedAt)}</span>
                    <span>{order.OrderItems.reduce((sum, item) => sum + item.quantity, 0)} articles</span>
                  </div>
                  <div className="panel-order-card-tags">
                    <span className={`panel-status-badge ${paymentBadge.className}`}>
                      {paymentBadge.label}
                    </span>
                    <span className="panel-status-badge is-neutral">
                      {getOrderStatusLabel(order.status)}
                    </span>
                    {order.fulfillmentType === 'delivery' ? <span className="panel-status-badge is-neutral">{getUberDirectStatusLabel(order.uberDirectStatus)}</span> : null}
                  </div>
                  <div className="panel-detail-list">
                    {order.OrderItems.map((item) => (
                      <article className="panel-line-item" key={`panel-history-item-${order.id}-${item.id}`}>
                        <div className="panel-line-item-top">
                          <strong>{item.quantity} x {item.kitchenName || item.name}</strong>
                          <span>{formatEuro(item.totalPrice)}</span>
                        </div>
                        {Array.isArray(item.selections) && item.selections.length > 0 && (
                          <div className="panel-line-item-sub">
                            {item.selections.map((selection, index) => (
                              <span key={`${order.id}-${item.id}-${index}`}>
                                {selection.kind === 'supplement'
                                  ? `+ ${selection.name}`
                                  : `${selection.sectionName}: ${selection.product?.name}`}
                              </span>
                            ))}
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                </article>
                );
              })
            )}
          </div>
        </section>
      ) : (
      <>
      <div className={`panel-layout ${visibleOrders.length === 0 ? 'is-empty' : ''}`}>
        <section className={`panel-orders ${visibleOrders.length === 0 ? 'is-empty' : ''}`}>
          {loading ? (
            <div className="panel-empty">Chargement des commandes...</div>
          ) : visibleOrders.length === 0 ? (
            <div className="panel-empty">Aucune commande dans cet onglet.</div>
          ) : (
            visibleOrders.map((order) => {
              const paymentBadge = getOrderPaymentBadge(order);
              return (
              <button
                key={order.id}
                type="button"
                className={`panel-order-card ${selectedOrderId === order.id ? 'is-active' : ''} ${
                  paymentBadge.className
                }`}
                onClick={() => {
                  setSelectedOrderId(order.id);
                  setCashReceived('');
                  setShowCashPaymentModal(false);
                  setMyposFeedback('');
                }}
                >
                <div className="panel-order-card-top">
                  <strong>Commande #{String(order.ticketNumber).padStart(4, '0')}</strong>
                  <span>{Number(order.totalTtc).toFixed(2)} €</span>
                </div>
                <div className="panel-order-card-meta">
                  <span>{formatDateTime(order.orderedAt)}</span>
                  <span>{order.OrderItems.reduce((sum, item) => sum + item.quantity, 0)} articles</span>
                </div>
                <div className="panel-order-card-tags">
                  <span className={`panel-status-badge ${paymentBadge.className}`}>
                    {paymentBadge.label}
                  </span>
                  <span className="panel-status-badge is-neutral">
                    {getOrderStatusLabel(order.status)}
                  </span>
                  {order.fulfillmentType === 'delivery' ? <span className="panel-status-badge is-neutral">{getUberDirectStatusLabel(order.uberDirectStatus)}</span> : null}
                </div>
              </button>
              );
            })
          )}
          <section className="panel-esp-status">
            <div className="panel-esp-status-head">
            <h3>Etat des Modules BE</h3>
            <span className="panel-esp-manager-state">
                {espBridgeStatus.managerReachable ? 'Connexion active' : 'Verification en cours'}
            </span>
          </div>
          {!espBridgeStatus.statuses.length ? (
              <div className="panel-empty">Aucun Module BE configure.</div>
          ) : (
              <div className="panel-esp-status-list">
                {espBridgeStatus.statuses.map((status) => (
                  <article className="panel-esp-status-item" key={`esp-bridge-${status.printerId}`}>
                    <div className="panel-esp-status-main">
                      <span className={`panel-esp-dot ${status.online ? 'is-up' : 'is-down'}`} />
                      <strong>{status.zoneName || status.printerName || status.bridgeId || 'Zone'}</strong>
                    </div>
                    <span className="panel-esp-status-meta">
                      {status.bridgeId ? `Module ${status.bridgeId}` : 'Module non renseigne'}
                    </span>
                    <span className="panel-esp-status-meta">
                      LED: {status.ledState?.colorLabel || 'Inconnue'} · {status.ledState?.stateLabel || 'Etat inconnu'}
                    </span>
                    <span className="panel-esp-status-meta">{status.actionHint}</span>
                  </article>
                ))}
              </div>
            )}
          </section>
        </section>

        <aside className="panel-detail">
          {!selectedOrder ? (
            <div className="panel-empty">Sélectionne une commande pour voir le détail.</div>
          ) : (
            <>
              <div className="panel-detail-head">
                <div>
                  <span className="admin-kicker">Commande</span>
                  <h2>Commande #{String(selectedOrder.ticketNumber).padStart(4, '0')}</h2>
                </div>
                <div className="panel-detail-total">{Number(selectedOrder.totalTtc).toFixed(2)} €</div>
              </div>

              <div className="panel-detail-meta">
                <span>Créée le {formatDateTime(selectedOrder.orderedAt)}</span>
                {selectedOrder.paidAt && <span>Payée le {formatDateTime(selectedOrder.paidAt)}</span>}
                {selectedOrder.completedAt && <span>Terminée le {formatDateTime(selectedOrder.completedAt)}</span>}
              </div>

              {selectedOrder.stripePrintStatus === 'uncertain' ? (
                <div className="panel-alert" role="alert">
                  Impression à vérifier. Contrôlez la cuisine avant toute réimpression.
                </div>
              ) : selectedOrder.stripePrintStatus === 'not_configured' ? (
                <div className="panel-alert" role="alert">
                  Aucune imprimante de production configurée. Commande disponible dans le KDS.
                </div>
              ) : null}

              {selectedOrder.fulfillmentType === 'delivery' ? (
                <div className="panel-payment-done">
                  <strong>Livraison Uber Direct</strong> · {getUberDirectStatusLabel(selectedOrder.uberDirectStatus)} · frais client {formatEuro(selectedOrder.deliveryFee)}
                  {selectedOrder.uberDirectDropoffEta ? ` · arrivée estimée ${formatDateTime(selectedOrder.uberDirectDropoffEta)}` : ''}
                  {selectedOrder.uberDirectTrackingUrl ? <>{' '}· <a href={selectedOrder.uberDirectTrackingUrl} target="_blank" rel="noreferrer">Suivre le coursier</a></> : null}
                  {selectedOrder.paymentStatus === 'paid' && !selectedOrder.uberDirectDeliveryId ? <>{' '}· <button type="button" className="admin-button admin-button-secondary" onClick={retryUberDirect} disabled={busyAction !== ''}>{busyAction === 'uber-direct-retry' ? 'Relance…' : 'Relancer Uber Direct'}</button></> : null}
                </div>
              ) : null}

              {(selectedOrder.customerComment || selectedOrder.includeCutlery) && (
                <div className="panel-payment-done">
                  {selectedOrder.customerComment ? `Commentaire : ${selectedOrder.customerComment}` : 'Sans commentaire'}
                  {selectedOrder.includeCutlery ? ' · Couverts demandés' : ''}
                </div>
              )}

              <div className="panel-actions-stack">
                <div className="panel-actions-block">
                  <h3>Actions</h3>
                  <div className="panel-action-row panel-action-row-wrap">
                    <button
                      type="button"
                      className="admin-button admin-button-secondary"
                      disabled={busyAction !== ''}
                      onClick={handleReprintClientTicket}
                    >
                      {busyAction === 'reprint-client' ? 'Réimpression...' : 'Réimprimer ticket client'}
                    </button>
                    {selectedOrder.status === 'en_cours' ? (
                      <button
                        type="button"
                        className="admin-button"
                        disabled={busyAction !== ''}
                        onClick={() => handleStatusChange('prete')}
                      >
                        {busyAction === 'status-prete' ? 'Validation...' : 'Marquer comme prête'}
                      </button>
                    ) : selectedOrder.status === 'prete' ? (
                      <>
                        <button
                          type="button"
                          className="admin-button"
                          disabled={busyAction !== ''}
                          onClick={() => handleStatusChange('terminee')}
                        >
                          {busyAction === 'status-terminee' ? 'Validation...' : 'Marquer comme terminée'}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button-secondary"
                          disabled={busyAction !== ''}
                          onClick={() => handleStatusChange('en_cours')}
                        >
                          {busyAction === 'status-en_cours' ? 'Retour...' : 'Remettre en préparation'}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        disabled={busyAction !== ''}
                        onClick={() => handleStatusChange('en_cours')}
                      >
                        {busyAction === 'status-en_cours' ? 'Réouverture...' : 'Rouvrir la commande'}
                      </button>
                    )}
                    {selectedOrder.paymentStatus !== 'paid' && !selectedOrderHasPendingMypos && selectedOrder.status !== 'annulee' ? (
                      <button
                        type="button"
                        className="admin-button admin-button-danger"
                        disabled={busyAction !== ''}
                        onClick={handleCancelCashTicket}
                      >
                        {busyAction === 'status-annulee' ? 'Annulation...' : 'Annuler ticket espèces'}
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="panel-actions-block">
                  <h3>Encaissement</h3>
                {selectedOrder.status === 'annulee' ? (
                  <div className="panel-payment-done">
                    Commande annulée. Encaissement désactivé.
                  </div>
                ) : selectedOrder.paymentStatus === 'paid' ? (
                  <div className="panel-payment-done">
                    Réglée en {selectedOrder.paymentMethod === 'espece' ? 'espèces' : 'CB'}
                    {selectedOrder.paymentMethod === 'espece' && selectedOrder.amountPaid ? (
                      <>
                        {' '}· Reçu {Number(selectedOrder.amountPaid).toFixed(2)} € · Rendu {Number(selectedOrder.changeDue || 0).toFixed(2)} €
                      </>
                    ) : null}
                  </div>
                ) : (
                  <>
                    {myposFeedback ? <div className="panel-payment-done">{myposFeedback}</div> : null}
                    <div className="panel-action-row panel-action-row-wrap">
                      <button
                        type="button"
                        className="admin-button"
                        disabled={busyAction !== ''}
                        onClick={() => handlePayment('cb')}
                      >
                        {busyAction === 'payment-cb' ? 'Encaissement...' : 'Encaisser en CB'}
                      </button>
                      <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        disabled={busyAction !== ''}
                        onClick={() => setShowCashPaymentModal(true)}
                      >
                        Encaisser en espèces
                      </button>
                      {selectedOrder.myposStatus === 'pending' ? (
                        <button
                          type="button"
                          className="admin-button admin-button-secondary"
                          disabled={busyAction !== ''}
                          onClick={handleCancelMyposPayment}
                        >
                          {busyAction === 'cancel-mypos' ? 'Annulation...' : 'Annuler le paiement TPE'}
                        </button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
              </div>

              <div className="panel-detail-list">
                {selectedOrder.OrderItems.map((item) => (
                  <article className="panel-line-item" key={item.id}>
                    <div className="panel-line-item-top">
                      <strong>{item.quantity} x {item.kitchenName || item.name}</strong>
                      <span>{Number(item.totalPrice).toFixed(2)} €</span>
                    </div>
                    {Array.isArray(item.selections) && item.selections.length > 0 && (
                      <div className="panel-line-item-sub">
                        {item.selections.map((selection, index) => (
                          <span key={`${item.id}-${index}`}>
                            {selection.kind === 'supplement'
                              ? `+ ${selection.name}`
                              : `${selection.sectionName}: ${selection.product?.name}`}
                          </span>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>
      </>
      )}

      {showEspWarningModal ? (
        <div className="panel-modal-backdrop" role="dialog" aria-modal="true">
          <div className="panel-modal panel-esp-warning-modal">
            <div className="panel-modal-head">
              <h3 className="panel-esp-warning-title">Attention imprimante</h3>
              <button
                type="button"
                className="admin-button admin-button-secondary"
                onClick={() => setEspWarningDismissedFor(offlineEspKey)}
              >
                Fermer
              </button>
            </div>
            <div className="panel-modal-body panel-esp-warning-body">
              <p>Un ou plusieurs Modules BE ne repondent pas correctement.</p>
              <p>Regarde la couleur de la LED sur le module concerne et suis l action indiquee.</p>
              <div className="panel-esp-warning-zones panel-esp-warning-zones-grid">
                {offlineEspStatuses.map((status) => (
                  <article className="panel-esp-warning-zone" key={`esp-warning-${status.printerId}`}>
                    <div className="panel-esp-warning-zone-head">
                      <strong>{status.zoneName || status.printerName || status.bridgeId || 'Zone'}</strong>
                      <span className={`panel-led-chip ${getEspLedClassName(status.ledState?.code)}`}>
                        {status.ledState?.colorLabel || 'Inconnu'}
                      </span>
                    </div>
                    <p>{status.ledState?.stateLabel || 'Etat non determine'}</p>
                    <p>{status.ledState?.hint || status.actionHint}</p>
                  </article>
                ))}
              </div>
              <div className="panel-led-help">
                <span><strong>Vert fixe</strong> Tout est OK.</span>
                <span><strong>Orange</strong> Imprimante non detectee: verifier USB et alimentation.</span>
                <span><strong>Bleu</strong> Wi-Fi OK mais serveur indisponible: redemarrer le Module BE.</span>
                <span><strong>Magenta</strong> Wi-Fi absent: se connecter a BEWIFI pour reconfigurer.</span>
                <span><strong>Rouge</strong> Erreur: verifier cables puis redemarrer le Module BE.</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showCashPaymentModal && selectedOrder && selectedOrder.paymentStatus !== 'paid' && selectedOrder.status !== 'annulee' ? (
        <div className="panel-modal-backdrop" role="dialog" aria-modal="true">
          <div className="panel-modal panel-modal-cash">
            <div className="panel-modal-head">
              <h3>Encaissement espèces</h3>
              <button
                type="button"
                className="admin-button admin-button-secondary"
                disabled={busyAction !== ''}
                onClick={closeCashPaymentModal}
              >
                Fermer
              </button>
            </div>
            <div className="panel-modal-body">
              <div className="panel-cash-line">
                <span>Commande #{String(selectedOrder.ticketNumber).padStart(4, '0')}</span>
                <strong>{Number(selectedOrder.totalTtc).toFixed(2)} €</strong>
              </div>
              <label className="admin-field">
                <span>Montant donné en espèces</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={cashReceived}
                  readOnly
                  placeholder={Number(selectedOrder.totalTtc).toFixed(2)}
                />
              </label>
              <NumericKeypad
                value={cashReceived}
                maxLength={5}
                compact
                className="panel-cash-keypad"
                onChange={setCashReceived}
              />
              <div className="panel-cash-summary">
                <span>
                  À rendre:{' '}
                  <strong>{cashChange !== null && cashChange >= 0 ? `${cashChange.toFixed(2)} €` : '--'}</strong>
                </span>
              </div>
            </div>
            <div className="panel-modal-actions">
              <button
                type="button"
                className="admin-button admin-button-secondary"
                disabled={busyAction !== ''}
                onClick={() => handlePayment('espece', { exactAmount: true })}
              >
                {busyAction === 'payment-espece' ? 'Encaissement...' : 'Encaisser espèces (montant exact)'}
              </button>
              <button
                type="button"
                className="admin-button"
                disabled={
                  busyAction !== '' ||
                  !cashReceived ||
                  !Number.isFinite(cashValue) ||
                  cashValue < Number(selectedOrder.totalTtc)
                }
                onClick={() => handlePayment('espece', { amountPaid: cashValue })}
              >
                {busyAction === 'payment-espece' ? 'Encaissement...' : 'Valider avec montant saisi'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {terminalOrderId ? <TerminalPaymentDialog orderId={terminalOrderId} fetchWithAuth={fetchWithAuth}
        onPaid={async () => { setMyposFeedback('Paiement confirmé sur le terminal.'); await fetchOrders(); }}
        onPrintReceipt={async payload => {
          const order = orders.find(row => Number(row.id) === Number(terminalOrderId));
          if (!order || order.paymentStatus !== 'paid') throw new Error('Actualisez la commande avant impression.');
          await handleClientTicketPrint(order, 'paid', payload.receipt || await getTerminalReceipt(fetchWithAuth, terminalOrderId, terminalScope));
        }}
        onClose={() => { setTerminalOrderId(null); fetchOrders(); }} /> : null}

      {panelDialog.isOpen ? (
        <div className="panel-modal-backdrop" role="dialog" aria-modal="true">
          <div className="panel-modal">
            <div className="panel-modal-head">
              <h3>{panelDialog.title || 'Information'}</h3>
            </div>
            <div className="panel-modal-body">
              <p>{panelDialog.message}</p>
              {panelDialog.requiresReason ? (
                <label>
                  Motif d’annulation
                  <input autoFocus type="text" value={dialogReason} maxLength={500} onChange={(event) => setDialogReason(event.target.value)} required />
                </label>
              ) : null}
            </div>
            <div className="panel-modal-actions">
              {typeof panelDialog.onConfirm === 'function' ? (
                <>
                  <button
                    type="button"
                    className="admin-button admin-button-secondary"
                    onClick={closePanelDialog}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handlePanelDialogConfirm}
                    disabled={panelDialog.requiresReason && !dialogReason.trim()}
                  >
                    {panelDialog.confirmLabel || 'Confirmer'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="admin-button admin-button-secondary"
                  onClick={closePanelDialog}
                >
                  {panelDialog.confirmLabel || 'Fermer'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PanelView;
